import type { SupportedLocale } from '../../i18n/supportedLocales'

/**
 * Result of resolving one banner string for the active UI locale.
 *
 * `fellBack` is reported explicitly rather than inferred by the caller comparing
 * `text` to the base string. A legitimate translation that happens to equal the
 * English (a product name, a URL, a number) would be indistinguishable from a
 * fallback under string comparison, which would make the NAD signal lie exactly
 * where it matters — a silent fallback renders as perfectly valid English and is
 * invisible in a screenshot.
 */
export interface ResolvedBannerText {
  text: string
  fellBack: boolean
}

/**
 * Pick the banner string for `locale`, falling back to the English base.
 *
 * Banner copy is stored as an English base column (`title` / `message`) plus a
 * per-locale override map (`title_i18n` / `message_i18n`) holding non-English
 * locales only. Resolution happens here, in the browser, rather than server-side:
 * the whole payload is already cached, so switching language is an instant
 * re-render with no refetch, no locale in the react-query key, and no dependence
 * on the API being reachable at switch time.
 *
 * Fallback ladder:
 *   - `en`                      → base (the map is not consulted; English IS the base)
 *   - locale present, non-blank → the override
 *   - locale absent / blank     → base, `fellBack: true`
 *
 * There is deliberately no cross-locale substitution: a Korean viewer with only a
 * Vietnamese override gets English, not Vietnamese.
 */
export function resolveBannerText(
  base: string,
  i18n: Record<string, string> | null | undefined,
  locale: SupportedLocale,
): ResolvedBannerText {
  // English is the base, not a fallback from it.
  if (locale === 'en') return { text: base, fellBack: false }
  // A missing map is the commonest untranslated case (every legacy row, every
  // operator-created banner). The viewer asked for another language and got
  // English, so this counts as a fallback — reporting otherwise would hide the
  // bulk of them from the signal.
  const value = i18n?.[locale]
  if (typeof value === 'string' && value.trim()) return { text: value, fellBack: false }
  return { text: base, fellBack: true }
}
